import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { AppSidebar } from './AppSidebar';
import { MobileBottomNav } from './MobileBottomNav';
import { AnimatedOutlet } from './AnimatedOutlet';
import { CitationsManagerPanel } from '@/components/citations';
import { EmailVerificationBanner } from '@/components/EmailVerificationBanner';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useUIStore } from '@/stores/uiStore';
import { useDevMode } from '@/hooks/useDevMode';
import { Wrench } from 'lucide-react';

export function AppLayout() {
  const { id: projectId } = useParams();
  const { toggleCitationsManager, citationsManagerProjectId } = useUIStore();
  const { isDevMode } = useDevMode();

  // Global keyboard shortcut for Citations Manager
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + Shift + C to toggle Citations Manager
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'c') {
        e.preventDefault();
        // Use current project ID if available
        toggleCitationsManager(projectId || citationsManagerProjectId || undefined);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [projectId, citationsManagerProjectId, toggleCitationsManager]);

  return (
    <div className="flex min-h-screen w-full bg-background">
      {isDevMode && (
        <div className="fixed top-2 right-2 z-50 flex items-center gap-1.5 px-2.5 py-1 bg-amber-500/90 text-amber-950 text-xs font-medium rounded-full shadow-lg">
          <Wrench className="w-3 h-3" />
          DEV MODE
        </div>
      )}
      
      {/* Desktop Sidebar - hidden on mobile */}
      <div className="hidden md:flex">
        <AppSidebar />
      </div>
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <EmailVerificationBanner />
        <ScrollArea className="flex-1">
          {/* Add bottom padding on mobile for the bottom nav */}
          <main className="pb-16 md:pb-0">
            <AnimatedOutlet />
          </main>
        </ScrollArea>
      </div>
      
      <CitationsManagerPanel />
      
      {/* Mobile Bottom Navigation - visible only on mobile */}
      <MobileBottomNav />
    </div>
  );
}
